import {
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import type { UploadedDocuments } from "Api/Mutations/Onboarding/useUploadIdentityDocuments";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { FilePreview } from "Components/Shared/FilePreview";
import {
  type DocumentCheckResultDto,
  IdentityDocumentSlot,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const StepResources = Resources.Onboarding.Steps.Documents;
const ReasonResources = StepResources.Declined.Reasons;

type Props = {
  checks: Array<DocumentCheckResultDto>;
  attemptsLeft: number;
  documents: UploadedDocuments;
  onRetry: () => void;
  onContinue: () => void;
};

const filesOf = (
  documents: UploadedDocuments,
  slot: IdentityDocumentSlot,
): Array<File> =>
  (slot === IdentityDocumentSlot.Secondary
    ? [documents.secondIDCardFrontSide, documents.secondIDCardBackSide]
    : [documents.firstIDCardFrontSide, documents.firstIDCardBackSide]
  ).filter((file): file is File => !!file);

/**
 * The automated check declined an upload: photograph the document again, or continue to the manual review.
 * Once the attempts are used up only the manual review remains.
 */
export const DocumentsDeclinedInfo = ({
  checks,
  attemptsLeft,
  documents,
  onRetry,
  onContinue,
}: Props) => {
  const { t } = useResource();
  const declined = checks.filter((check) => check.requiresManualReview);
  const showDocumentTitles = checks.length > 1;
  const canRetry = attemptsLeft > 0;

  return (
    <StepBox
      submitButtonLabel={t(
        canRetry
          ? StepResources.Declined.Retry
          : StepResources.Declined.Continue,
      )}
      onSubmitClick={canRetry ? onRetry : onContinue}
      secondaryButtonLabel={
        canRetry ? t(StepResources.Declined.Continue) : undefined
      }
      onSecondaryButtonClick={canRetry ? onContinue : undefined}
    >
      <Alert severity="warning" sx={{ mt: 2 }}>
        <AlertTitle>{t(StepResources.Declined.Title)}</AlertTitle>
        <Stack gap={1}>
          {declined.map((check) => (
            <div key={check.document}>
              {showDocumentTitles && (
                <Typography variant="subtitle2">
                  {t(
                    check.document === IdentityDocumentSlot.Secondary
                      ? StepResources.SecondDocumentTitle
                      : StepResources.FirstDocumentTitle,
                  )}
                </Typography>
              )}
              <Stack direction="row" gap={1} my={1}>
                {filesOf(documents, check.document).map((file) => (
                  <FilePreview key={file.name} file={file} />
                ))}
              </Stack>
              <List dense disablePadding>
                {check.reasons.map((reason) => (
                  <ListItem key={reason} disableGutters sx={{ py: 0 }}>
                    <ListItemText primary={t(ReasonResources[reason])} />
                  </ListItem>
                ))}
              </List>
            </div>
          ))}
          <Typography variant="body2">
            {t(
              canRetry
                ? StepResources.Declined.Description
                : StepResources.Declined.AttemptsExhausted,
            )}
          </Typography>
          {canRetry && (
            <Typography variant="body2">
              {t(StepResources.Declined.AttemptsLeft, { count: attemptsLeft })}
            </Typography>
          )}
        </Stack>
      </Alert>
    </StepBox>
  );
};
